export const defaultTaxConfig = {
	taxEnabled: false,
	taxType: "",
	applyToMedicine: false,
	applyToOtc: false,
};

export const getTaxAppliedToValue = (applyToMedicine, applyToOtc) => {
	if (applyToMedicine && applyToOtc) return "both";
	if (applyToMedicine) return "medicine";
	if (applyToOtc) return "otc";
	return "";
};

export const getTaxConfigFromPharmacyPOS = (pharmacyPOS) => {
	if (!pharmacyPOS) return defaultTaxConfig;

	const rawTaxType = String(pharmacyPOS.taxType || "").toLowerCase();

	if (!rawTaxType || rawTaxType === "none") {
		return defaultTaxConfig;
	}

	const taxType = rawTaxType === "vat" ? "vat" : rawTaxType === "gst" ? "gst" : "";

	if (!taxType) {
		return defaultTaxConfig;
	}

	const taxAppliedTo = String(pharmacyPOS.taxAppliedTo || "").toLowerCase();

	return {
		taxEnabled: true,
		taxType,
		applyToMedicine: taxAppliedTo === "medicine" || taxAppliedTo === "both",
		applyToOtc: taxAppliedTo === "otc" || taxAppliedTo === "both",
	};
};

/**
 * Whether per-item tax % should be shown for add/edit item forms.
 * Medicine and Unani follow the "Medicine" scope from pharmacy settings.
 */
export const shouldShowItemTaxField = (pharmacyPOS, itemType = "medicine") => {
	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	if (!config.taxEnabled) return false;

	const normalizedType = String(itemType || "medicine").toLowerCase();
	if (normalizedType === "otc") return config.applyToOtc;

	return config.applyToMedicine;
};

export const isPharmacyTaxEnabled = (pharmacyPOS) =>
	getTaxConfigFromPharmacyPOS(pharmacyPOS).taxEnabled;

export const getActivePosTaxType = (pharmacyPOS, fallback = "gst") => {
	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	return config.taxEnabled ? config.taxType : fallback;
};

const isIndiaCountryName = (country) => {
	const text = String(
		typeof country === "object"
			? country?.label || country?.value || country?.code || ""
			: country || ""
	)
		.toLowerCase()
		.trim();
	return text === "india" || text === "ind" || text === "in";
};

const hasCountrySelection = (country) =>
	Boolean(
		String(
			typeof country === "object"
				? country?.label || country?.value || ""
				: country || ""
		).trim()
	);

/**
 * Tax registration number label.
 * - India → GST Number
 * - Other country → Tax Number (or VAT Number if pharmacy tax type is VAT)
 * - No country → pharmacy tax config (GST / VAT / Tax Number)
 */
export const getTaxNumberLabel = (pharmacyPOS, t = (key) => key, country = null) => {
	if (hasCountrySelection(country)) {
		if (isIndiaCountryName(country)) return t("GST Number");
		const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
		if (config.taxType === "vat") return t("VAT Number");
		return t("Tax Number");
	}

	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	if (config.taxType === "vat") return t("VAT Number");
	if (config.taxType === "gst") return t("GST Number");
	return t("Tax Number");
};

/** UI label for state/region tax code field based on pharmacy tax config. */
export const getStateTaxCodeLabel = (pharmacyPOS, t = (key) => key, country = null) => {
	if (hasCountrySelection(country)) {
		if (isIndiaCountryName(country)) return t("State Code (GST)");
		return t("State Code");
	}

	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	if (config.taxType === "vat") return t("State Code");
	return t("State Code (GST)");
};

/** Menu / page title for tax diary based on pharmacy tax config. */
export const getTaxDiaryLabel = (pharmacyPOS, t = (key) => key) => {
	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	if (config.taxType === "vat") return t("VAT Diary");
	return t("GST Diary");
};

export const getTaxNumberPlaceholder = (pharmacyPOS, t = (key) => key, country = null) => {
	if (hasCountrySelection(country)) {
		if (isIndiaCountryName(country)) return t("Enter GST number");
		const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
		if (config.taxType === "vat") return t("Enter VAT number");
		return t("Enter tax number");
	}

	const config = getTaxConfigFromPharmacyPOS(pharmacyPOS);
	if (config.taxType === "vat") return t("Enter VAT number");
	if (config.taxType === "gst") return t("Enter GST number");
	return t("Enter tax number");
};

export const getStockItemType = (item) => {
	if (item?.isOTC === true || item?.isOTC === "true") return "otc";
	if (item?.isUnani === true || item?.isUnani === "true" || item?.type === "unani") {
		return "unani";
	}
	return "medicine";
};

export const shouldApplyTaxToStockItem = (pharmacyPOS, item) =>
	shouldShowItemTaxField(pharmacyPOS, getStockItemType(item));

export const shouldShowPurchaseTaxUi = (pharmacyPOS, { purchaseTax = 0, items = [] } = {}) => {
	if (isPharmacyTaxEnabled(pharmacyPOS)) return true;
	if (Number(purchaseTax) > 0) return true;
	return items.some(
		(item) => Number(item?.taxPercentage ?? item?.tax ?? 0) > 0
	);
};

/** Whether sale / return view should show tax columns and breakdown. */
export const shouldShowSaleTaxUi = (pharmacyPOS, { saleTax = 0, items = [] } = {}) => {
	if (isPharmacyTaxEnabled(pharmacyPOS)) return true;
	if (Number(saleTax) > 0) return true;
	return items.some(
		(item) =>
			Number(item?.taxPercentage ?? item?.tax ?? 0) > 0 ||
			Number(item?.CGST ?? 0) > 0 ||
			Number(item?.SGST ?? 0) > 0 ||
			Number(item?.IGST ?? 0) > 0 ||
			Number(item?.VAT ?? 0) > 0
	);
};

/** Whether POS cart should show tax columns / tax summary. */
export const shouldShowPosTaxUi = (pharmacyPOS, cart = []) => {
	if (!isPharmacyTaxEnabled(pharmacyPOS)) return false;
	if (!cart.length) return false;
	return cart.some((item) => shouldApplyTaxToStockItem(pharmacyPOS, item));
};

export const getCartItemForTaxCalculation = (pharmacyPOS, item) => {
	if (!shouldApplyTaxToStockItem(pharmacyPOS, item)) {
		return { ...item, taxPercentage: 0 };
	}
	return item;
};
